import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "AITIM Group",
  "url": "https://aitimgroup.com",
  "logo": "https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp",
  "founder": {
    "@type": "Person",
    "name": "Timur Tilenov"
  },
  "sameAs": [
    "https://www.facebook.com/aitiminc/",
    "https://www.instagram.com/aitim_group/",
    "https://www.linkedin.com/company/aitim"
  ]
};

const About = () => {
  const { t, language } = useLanguage();
  const isRu = language === 'ru';

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={t('about.heroTitle')}
        description={t('about.heroSubtitle')}
        canonical="/about"
        schemaJson={organizationSchema}
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=768" 
              srcSet="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=480 480w, https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=768 768w, https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=1200 1200w"
              sizes="(max-width: 768px) 100vw, 1200px"
              alt="About AITIM Group" 
              width={1200}
              height={800}
              fetchPriority="high"
              className="w-full h-full object-cover object-center opacity-40"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {isRu ? 'О нас' : 'About Us'}
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                {t('about.heroTitle')}
              </h1>
              <p className="text-lg lg:text-2xl text-white/90 mb-10 font-medium leading-relaxed max-w-3xl mx-auto">
                {t('about.heroSubtitle')}
              </p>
              <Link to="/free-consultation">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto">
                  {isRu ? 'Связаться с нами' : 'Get In Touch'} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* About Content Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-navy-950 mb-8 tracking-tight text-center">{t('about.aboutTitle')}</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  {t('about.aboutDesc')}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <a href="https://www.instagram.com/aitim_group/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-navy-950 hover:bg-primary hover:text-white transition-all duration-300">
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a href="https://www.facebook.com/aitiminc/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-navy-950 hover:bg-primary hover:text-white transition-all duration-300">
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a href="https://www.linkedin.com/company/aitim" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-navy-950 hover:bg-primary hover:text-white transition-all duration-300">
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a href="https://t.me/aitim_sales" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-navy-950 hover:bg-primary hover:text-white transition-all duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.19-.08-.05-.19-.02-.27 0-.12.03-1.98 1.26-5.6 3.71-.53.36-1.01.53-1.44.52-.47-.01-1.38-.27-2.05-.49-.83-.27-1.49-.41-1.43-.87.03-.24.36-.49.99-.75 3.88-1.69 6.47-2.8 7.76-3.34 3.69-1.54 4.46-1.81 4.96-1.82.11 0 .36.03.52.16.14.12.18.28.2.42.02.11.02.24.01.38z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 bg-slate-50 border-y border-slate-100">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-black text-navy-950 mb-4 tracking-tight">{t('about.statsTitle')}</h2>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {[
                { value: "10+", label: t('about.statsExp') },
                { value: "7000+", label: t('about.statsTrucks') },
                { value: "100%", label: t('about.statsSat') },
                { value: "20+", label: t('about.statsAwards') },
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <div className="text-4xl md:text-6xl font-light text-primary mb-4 group-hover:scale-110 transition-transform duration-300">{stat.value}</div>
                  <div className="h-0.5 bg-slate-200 w-full mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                  </div>
                  <div className="text-xs font-bold tracking-widest text-slate-500 uppercase">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-center gap-16 max-w-6xl mx-auto">
              <div className="lg:w-1/3">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl"></div>
                  <video
                    src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/6a1095bcb656b3edc444b915.webm"
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-auto rounded-3xl shadow-2xl relative z-10"
                  />
                </div>
              </div>
              <div className="lg:w-2/3">
                <div className="mb-8">
                  <h3 className="text-3xl font-bold text-navy-950 mb-2">{t('about.founderName')}</h3>
                  <div className="text-primary font-semibold uppercase tracking-wider text-sm">{t('about.founderTitle')}</div>
                </div>
                <blockquote className="text-2xl lg:text-3xl font-medium text-slate-700 italic leading-relaxed">
                  {t('about.founderQuote')}
                </blockquote>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
